import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {lookupInferredTenantInfo} from 'benchling-apps/BenchlingAppActions';
import {
  isTenantInferredInfoError,
  TenantInferredInfo,
  TenantInferredInfoError,
  UpdateBenchlingAppDefinitionVersionArgs,
} from 'benchling-apps/BenchlingAppDAO';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_LIGHT, GRAY_LIGHTER, GREEN_DARK, hexToRgba, RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {InputChangeEvent} from 'util/Types';

import {AppDefinitionVersion} from './BenchlingAppsPage';

type VersionAllowList = AppDefinitionVersion['allowList'];

// Make this a function so that we can stub this in tests.
const maxEntries = (): number => BenchlingConfig.BENCHLING_APP_LIMITED_AVAILABILITY_ALLOW_LIST_MAX;
const TENANT_ID_PREFIX = 'ten_';
const HTTPS_PREFIX = 'https://';
const DEFAULT_HOST_URL = 'https://<HOST>';
// see benchling/lib/url.py for definition of BENCHLING_TENANT_DOMAINS
const BENCHLING_TENANT_DOMAINS = ['benchling.com', 'bnch.org', 'bnchdev.org', 'bnch.us:5000'];
const SHORTEST_DOMAIN_LENGTH = Math.min(...BENCHLING_TENANT_DOMAINS.map((domain) => domain.length));
const SHORTEST_QUERY = HTTPS_PREFIX.length + SHORTEST_DOMAIN_LENGTH;

export interface ManageAllowListProps {
  appDefinitionId: string;
  allowList: VersionAllowList;
  onRequestClose: () => void;
  handleSubmitDefinitionVersion: (newValue: UpdateBenchlingAppDefinitionVersionArgs) => void;
}

export interface UpdateAllowListEntry extends TenantInferredInfo {
  isUnsaved: boolean;
}

export default function ManageAllowList(props: ManageAllowListProps): JSX.Element {
  const initialAllowList = props.allowList.map(
    (item) => ({id: item.tenantId, isUnsaved: false}) as UpdateAllowListEntry
  );
  const [query, setQuery] = useState<string | null>(null);
  const [addEnabled, setAddEnabled] = useState<boolean>(false);
  const [newId, setNewId] = useState<string | null>(null);
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [allowList, setAllowList] = useState<Array<UpdateAllowListEntry>>(initialAllowList);
  const [lastQueryTimestamp, setLastQueryTimestamp] = useState<Date>(new Date());

  const includesTenantDomain = useCallback(
    (url: string): boolean => BENCHLING_TENANT_DOMAINS.some((domain) => url.toLowerCase().includes(domain)),
    []
  );

  const tenantIsAlreadyAllowed = useCallback(
    (id: string): boolean => {
      const matches = allowList.filter((entry) => entry.id === id);
      return matches.length > 0;
    },
    [allowList]
  );

  const lookupUrl = useCallback(async (url: string): Promise<TenantInferredInfo | null> => {
    // Makes a request from the back-end to look up tenant info from {HOST}/debug/info.
    // Uses sentTimestamp to ignore obsolete results that arrive out of order.
    // Returns a TenantInferredInfo if lookup is successful.
    // Returns null if lookup failed for any reason.
    const sentTimestamp = new Date();
    let info: TenantInferredInfo | TenantInferredInfoError | null = null;
    try {
      info = await lookupInferredTenantInfo(url);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      // Ignore failed lookups
    }
    if (sentTimestamp > lastQueryTimestamp) {
      // record this result's timestamp and ignore any queries that might have been created earlier
      setLastQueryTimestamp(sentTimestamp);
      if (info == null || isTenantInferredInfoError(info)) {
        return null;
      }
      return info;
    }
    // another newer query has arrived while we were waiting, so ignore this result
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (allowList.length >= maxEntries()) {
      setNewId(null);
      setNewUrl(null);
      setAddEnabled(false);
      setQuery(null);
      return;
    }
    if (query && query.length > TENANT_ID_PREFIX.length + 1 && query.startsWith(TENANT_ID_PREFIX)) {
      setNewId(query);
      setNewUrl(null);
      setAddEnabled(!tenantIsAlreadyAllowed(query));
      return;
    }
    let queryUrl = query || '';
    if (query && query.length > HTTPS_PREFIX.length && !query.startsWith(HTTPS_PREFIX)) {
      queryUrl = `${HTTPS_PREFIX}${query}`;
    }

    if (queryUrl.length > SHORTEST_QUERY && includesTenantDomain(queryUrl)) {
      lookupUrl(queryUrl).then((result) => {
        if (result == null) {
          setNewId(null);
          setNewUrl(null);
          setAddEnabled(false);
        } else {
          setNewId(result.id);
          setNewUrl(result.url);
          setAddEnabled(result.id != null && !tenantIsAlreadyAllowed(result.id));
        }
      });
      return;
    }
    // Query string is too short or invalid
    setNewId(null);
    setNewUrl(null);
    setAddEnabled(false);
  }, [query, allowList.length, lookupUrl, tenantIsAlreadyAllowed, includesTenantDomain]);

  function onAdd(): void {
    if (newId == null || tenantIsAlreadyAllowed(newId)) {
      // Add button should be disabled when newId is null or already allowed,
      // so this case should never happen.
      notifications.error('Something went wrong, please try again.');
      return;
    }
    setAllowList([...allowList, {id: newId, url: newUrl, isUnsaved: true}]);
  }

  function onRemove(id: string): void {
    setAllowList(allowList.filter((item) => item.id !== id));
  }

  function onSubmit(): void {
    if (allowList.length > maxEntries()) {
      // This should never happen, enforced by form input validation.
      throw new Error(`Cannot exceed limit of ${maxEntries()} tenants.`);
    }
    const newAllowList = allowList.map((item) => item.id);
    props.handleSubmitDefinitionVersion({allowList: newAllowList});
    props.onRequestClose();
  }

  return (
    <Modal
      onRequestClose={props.onRequestClose}
      onRequestSubmit={onSubmit}
      title="Share with tenants"
      buttons={[
        {
          text: 'Save',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !allowList.some((item) => item.isUnsaved),
        },
      ]}
    >
      <p>Manage tenants that can install your app. Generate install URLs for up to 10 tenants.</p>
      <Row>
        <Col sm={6}>
          <PlainSearchInput
            value={query}
            clearType={ClearType.X}
            onChange={(e: InputChangeEvent) => {
              setQuery(e.target.value);
            }}
            onEscape={() => {
              setQuery(null);
            }}
            placeholder="Tenant URL or ID"
            disabled={allowList.length >= maxEntries()}
          />
        </Col>
        <Col>
          <Tooltip
            container="body"
            placement={TooltipPlacement.TOP}
            title={
              allowList.length >= maxEntries()
                ? 'You have added the maximum number of tenants'
                : !addEnabled
                  ? newId
                    ? 'This tenant has already been added'
                    : 'Enter a tenant URL or ID'
                  : `Permanently add ${newId} to this app's Allow List.`
            }
          >
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.SMALL}
              text="Add"
              disabled={!addEnabled}
              onClick={onAdd}
            />
          </Tooltip>
        </Col>
      </Row>
      {query && query.length > 5 ? <AllowListMatch id={newId} /> : null}
      {allowList.length >= maxEntries() && <LimitExceededBanner />}
      <AllowListTable appDefinitionId={props.appDefinitionId} rows={allowList} onRemove={onRemove} />
    </Modal>
  );
}

export function LimitExceededBanner(): JSX.Element {
  return (
    <div className={css(styles.limitExceededBanner)}>
      <InlineBanner
        bannerType={InlineBannerType.INFO}
        text="You’ve reached the limit"
        supportingInfo={`Share the app version with up to ${maxEntries()} ${pluralize(maxEntries(), 'tenant', 'tenants')}.
    Manage your list to share with different tenants or contact support to have your limit increased.`}
      />
    </div>
  );
}

export function AllowListMatch({id}: {id: string | null}): JSX.Element {
  return id == null ? (
    <Row className={css(styles.allowListRow)}>
      <BenchlingIcon icon="question" color={GRAY_DARK} className={css(styles.inline)} />
      <div className={css(styles.inline)}>Unknown tenant</div>
    </Row>
  ) : (
    <Row className={css(styles.allowListRow)}>
      <BenchlingIcon icon="check" color={GREEN_DARK} className={css(styles.inline)} />
      <div className={css(styles.inline)}>
        <b>ID:</b> {id}
      </div>
    </Row>
  );
}

export function AllowListTable({
  appDefinitionId,
  rows,
  onRemove,
}: {
  appDefinitionId: string;
  rows: Array<TenantInferredInfo>;
  onRemove: (id: string) => void;
}): JSX.Element {
  return (
    <div className={css(styles.allowListTable)}>
      <Row className={css(styles.allowListHeader)}>
        <Col md={2}>Tenant ID</Col>
        <Col md={10}>Install URL</Col>
      </Row>
      {rows.map((row: UpdateAllowListEntry, i) => {
        const host = row.url ?? DEFAULT_HOST_URL;
        const unsavedStyle = row.isUnsaved ? styles.newEntry : null;

        const removeTitle = row.isUnsaved ? 'Remove this tenant' : 'Access to this tenant cannot be removed';
        const removeStyle = row.isUnsaved ? css(styles.removeIcon) : css(styles.hidden);
        const removeButton = (
          <Tooltip container="body" placement={TooltipPlacement.TOP} title={removeTitle}>
            {row.isUnsaved ? (
              <BenchlingIcon
                icon="close"
                className={removeStyle}
                dataTestAttrs={{component: 'ManageAllowList', key: 'removeTenant'}}
                onClick={() => {
                  onRemove(row.id);
                }}
              />
            ) : (
              <div className={css(styles.hidden)} />
            )}
          </Tooltip>
        );

        return (
          <Row key={i} className={css(styles.allowListRow, unsavedStyle)}>
            <Col md={2}>{row.id}</Col>
            <Col md={9}>{urlForRow(appDefinitionId, host)}</Col>
            <Col md={1}>{removeButton}</Col>
          </Row>
        );
      })}
    </div>
  );
}

function urlForRow(appDefinitionId: string, host: string): JSX.Element {
  const maxLength = 56;
  const installUrl = `${host}/app-catalog/apps/${appDefinitionId}/install`;
  const clippedUrl =
    installUrl.length > maxLength ? <>{installUrl.substring(0, maxLength)}&hellip;</> : installUrl;
  return (
    <>
      <Tooltip container="body" placement={TooltipPlacement.TOP} title="Copy install URL">
        <Button
          buttonStyle={ButtonStyle.CIRCLE_ICON}
          size={ButtonSize.MINI}
          className={css(styles.copyToClipboardIcon)}
          icon="copy"
          onClick={() =>
            copyToClipboard(installUrl, {
              showToastOnSuccess: true,
              toastText: `Copied install URL to clipboard: ${installUrl})}`,
            })
          }
        />
      </Tooltip>
      <a target="_blank" href={installUrl} rel="noreferrer">
        {clippedUrl}
      </a>
    </>
  );
}

const styles = StyleSheet.create({
  allowListHeader: {
    backgroundColor: GRAY_LIGHTER,
    fontWeight: 'bold',
    padding: 10,
  },
  unknownName: {
    color: GRAY_DARK,
    fontStyle: 'italic',
  },
  inline: {
    display: 'inline-block',
    marginLeft: 15,
  },
  allowListRow: {
    padding: 10,
    ':hover': {
      backgroundColor: GRAY_LIGHT,
    },
  },
  allowListTable: {
    marginTop: '1em',
  },
  grayBox: {
    backgroundColor: GRAY_LIGHTER,
    padding: 10,
  },
  limitExceededBanner: {
    marginTop: 16,
    marginBottom: 8,
  },
  copyToClipboardIcon: {
    display: 'inline-block',
    color: GRAY_DARK,
  },
  hidden: {
    height: 24,
    width: 20,
    backgroundColor: hexToRgba('#FFFFFF', 0),
  },
  removeIcon: {
    display: 'inline-block',
    color: GRAY_DARK,
    ':hover': {
      color: RED,
    },
  },
  newEntry: {
    color: '#000',
  },
});
